/**
 * ProtectedRoute — wraps any route that requires authentication.
 * If the user isn't signed in, redirects to /login (with `from` query param
 * so we can bounce them back after sign-in).
 *
 * Usage:
 *   <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { DeletionGate } from './DeletionPending';

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Minimal splash while Supabase rehydrates the session from localStorage.
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-bg">
        <div className="font-mono text-xs uppercase tracking-widest text-ink-muted">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-primary-bright" />
          Loading…
        </div>
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <DeletionGate>{children}</DeletionGate>;
}
